<template>
    <div class="row">
        <div class="col-12 mb-4 d-flex flex-row align-items-center">
            <h1 class="mb-0">{{ !groupType.id ? $t('new-grouping') : $t('edit-grouping') }}</h1>
            <button type="button" class="btn btn-primary ml-auto" @click="saveTypeAndGroups">{{ $t('save') }}</button>
            <button v-if="groupType.id && !groupType.published" type="button" class="btn btn-secondary ml-2" @click="publishType">{{ $t('publish') }}</button>
        </div>

        <div class="col-12 mb-4" :class="{'col-lg-7': !groupType.published}">
            <div v-if="published || saved" class="alert alert-success mb-3">
                {{ published ? $t('group-type-published') : $t('group-type-saved') }}
            </div>

            <!-- Group Type Details -->
            <TextInput :label="$t('title')" v-model="groupType.title" />
            <SelectInput
                v-if="!groupType.id"
                :value="groupType.programme_id"
                :options="$config.getValue('programmes')"
                :label="$t('programme')"
                optionLabel="Name"
                optionValue="Id"
                @input="updateProgramme"
            />

            <!-- Groups -->
            <div v-if="groupType.id && !loading" class="list-group border-radius-2 no-border mt-4">
                <div class="list-group-item font-weight-bold">
                    {{ programme.Name }}
                </div>
                <YearGroupRow 
                    v-for="i in Number(programme.CourseLengthYears)" 
                    :key="i"
                    :year="i"
                    :groups="groupsByYear[`${i}`]"
                    @removeGroup="removeGroup"
                    @selectGroup="editGroup"
                    :editable="!groupType.published"
                />
            </div>
        </div>

        <div v-if="groupType.id && !loading && !groupType.published" class="col-12 col-lg-5 mb-4">

            <!-- Add Groups -->
            <div class="list-group border-radius-2 no-border">
                <div class="list-group-item clearfix">
                    <div class="d-flex flex-row align-items-center justify-content-between mb-3">
                        <h3 class="mb-0">{{ $t('add-groups') }}</h3>
                        <button 
                            type="button" 
                            class="btn btn-primary float-right"
                            @click="addGroups"
                        >
                            {{ $t('add') }}
                        </button>
                    </div>
                    <TextInput v-model="newGroupTitle" :label="$t('title')" class="mb-3"/>
                    
                    <label>{{ $t('years') }}</label>
                    <YearMultiSelect v-model="newGroupYears" :useProgrammes="[programme]" :inline="true" />
                </div>
            </div>
        </div>
        
    </div>
</template>

<script>
import TextInput from "src/components/Inputs/TextInput.vue"
import SelectInput from "src/components/Inputs/SelectInput.vue"
import YearMultiSelect from "src/components/ProgrammeYearGroups/YearMultiSelect.vue"
import YearGroupRow from "../../../components/GroupTypes/YearGroupRow.vue"

export default {
    components: {
        SelectInput,
        TextInput,
        YearMultiSelect,
        YearGroupRow,
    },
    mounted() {
        this.$store.commit('showBlockingSpinner')
        
        Promise.all([this.fetchType(), this.fetchGroups()]).then(() => {
            this.loading = false
            this.$store.commit('hideBlockingSpinner')
        })
    },
    data() {
        return {
            loading: true,
            submitting: false,
            groupType: {
                id: Number(this.$route.params.id) ? Number(this.$route.params.id) : undefined,
                title: '',
                programme_id: null,
                published: false,
            },
            groups: [],
            groupsByYear: {},
            programme: null,
            newGroupTitle: '',
            newGroupYears: [],
            saved: false,
            published: false,
        }
    },
    methods: {
        fetchType() {
            if (!this.groupType.id) return Promise.resolve()

            return this.$cfetch(`${this.$settings.APIBaseURL}/grouptypes/get`, {
                method: 'GET',
                data: { id: this.groupType.id },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error('Unexpected error fetching group type')

                this.groupType = res.result

                if (this.groupType.programme_id) {
                    this.programme = this.$config.getValue('programmes')[this.groupType.programme_id]
                }
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-group-type'),
                })
            })
        },
        fetchGroups() {
            if (!this.groupType.id) return Promise.resolve()

            return this.$cfetch(`${this.$settings.APIBaseURL}/grouptypes/yeargroups/all`, {
                method: 'GET',
                data: { grouptype_id: this.groupType.id },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error('Unexpected error fetching group type')

                this.groups = res.results
                this.sortGroupsByYear()
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-groups'),
                })
            })
        },
        saveTypeAndGroups() {
            if (this.submitting) return
            this.submitting = true

            this.saved = false
            this.published = false

            this.$store.commit('showBlockingSpinner')

            return Promise.all([
                this.saveType(),
                this.saveGroups(),
            ]).then(() => {
                this.saved = true
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-saving-group-type'),
                })
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
                this.submitting = false
            })
        },
        saveType(publish=false) {
            return this.$cfetch(`${this.$settings.APIBaseURL}/grouptypes/save`, {
                method: 'POST',
                data: { 
                    id: this.groupType.id,
                    programme_id: this.groupType.programme_id,
                    title: this.groupType.title,
                    published: publish
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error()

                if (!this.groupType.id) this.$router.push(`/settings/grouptypes/${res.result.id}`)
                else this.groupType = res.result
            })
        },
        saveGroups() {
            // This bulk group-save is only for unpublished group types
            if (this.groupType.published) return Promise.resolve()

            return this.$cfetch(`${this.$settings.APIBaseURL}/grouptypes/unpublished/savegroups`, {
                method: 'POST',
                data: {
                    grouptype_id: this.groupType.id,
                    groups: this.groups,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error()
            })
        },
        publishType() {
            if (this.submitting || this.groupType.published || this.loading) return

            this.saved = false
            this.published = false

            this.$store.commit('openDialog', {
                title: this.$t('publish-group-type-?'),
                content: this.$t('publish-group-type-message'),
                cancelBtn: true,
                confirmCallBack: async () => {
                    this.$store.commit('showBlockingSpinner')
                    let groupsSaved = true

                    // We need to make sure the type's groups are saved before publishing,
                    // as bulk group changes are not permitted on published group types
                    await this.saveGroups().catch(err => {
                        groupsSaved = false
                        console.error(err)
                        this.$log.error.call(this, err)

                        this.$store.commit('openDialog', {
                            title: this.$t('something-went-wrong'),
                            content: this.$t('error-saving-group-type'),
                        })
                    })

                    if (!groupsSaved) {
                        this.$store.commit('hideBlockingSpinner')
                        return
                    }

                    await this.saveType(true).then(() =>{
                        this.published = true
                    }).catch(err => {
                        console.error(err)
                        this.$log.error.call(this, err)

                        this.$store.commit('openDialog', {
                            title: this.$t('something-went-wrong'),
                            content: this.$t('error-saving-group-type'),
                        })
                    })

                    this.$store.commit('hideBlockingSpinner')
                }
            })
        },
        sortGroupsByYear() {
            this.groupsByYear = {}
            this.groups.forEach(group => {
                if (!this.groupsByYear[`${group.year}`]) this.groupsByYear[`${group.year}`] = []
                this.groupsByYear[`${group.year}`].push(group)
            })
        },
        updateProgramme(programmeId) {
            this.groupType.programme_id = programmeId
            this.programme = this.$config.getValue('programmes')[this.groupType.programme_id]
        },
        addGroups() {
            this.newGroupYears.forEach(progYear => {
                this.groups.push({
                    title: `${this.newGroupTitle}`,
                    year: progYear.year,
                })
            })
            this.sortGroupsByYear()
        },
        removeGroup(group) {
            this.groups = this.groups.filter(el => !(el.id == group.id && el.title == group.title && el.year == group.year))
            this.sortGroupsByYear()
        },
        editGroup(group) {
            if (!this.groupType.published || !group.id) return

            this.$router.push(`/settings/grouptypes/${this.groupType.id}/groups/${group.id}`);
        }
    },
}
</script>